import React from 'react'
import Modal from 'react-bootstrap/Modal'

import Backend from "../../../../utils/Backend"
import Notify from "../../../../utils/Notify"
import Phone from "../../../../utils/Phone"

import CustomSelect from "../../common/CustomSelect"
import CountrySelect, { isCountryCodeAllowedForRegion } from "../../common/CountrySelect"
import PhoneInput from "../../common/PhoneInput"

const ADYEN_STATE_REQUIRED_COUNTRIES = ["AU", "CA", "BR", "IN", "MX", "NZ", "US"]

const REGION_OPTIONS = [
  {
    label: "EU (Europe)",
    value: "eu"
  },
  {
    label: "NA (North America / US)",
    value: "na"
  }
]

const CURRENCY_OPTIONS = [
  {
    label: "EUR",
    value: "EUR"
  },
  {
    label: "GBP",
    value: "GBP"
  }
]

export default class AdyenConnectProcessor extends React.Component {
  constructor(props){
    super(props)

    this.state = {
      ...this._getState(props),
      connectedRegions: {},
      defaultRegion: null,
      industryCodes: {},
      loadingStatus: false,
      loading: false,
      showForm: true,
      isEditing: false,
    }
  }

  componentDidMount(){
    this.setState({ loadingStatus: true })
    Promise.all([
      this._loadIndustryCodes(),
      this._loadConnectStatus(),
    ])
    .finally(() => {
      this.setState({ loadingStatus: false })
    })
  }

  _getState(props){
    return {
      ...props,
      processor: props.processor,
      data: this._normalizeData(props.data),
    }
  }

  _normalizeData(data){
    data = data || {}
    let address = data.address || {}

    return {
      legal_name: data.legal_name || "",
      industry_code: data.industry_code || "",
      phone: data.phone || "",
      shopper_statement: data.shopper_statement || "",
      default_currency_code: data.default_currency_code || "",
      region: data.region || "eu",
      address: {
        country: address.country || "",
        line_1: address.line_1 || "",
        line_2: address.line_2 || "",
        line_3: address.line_3 || "",
        city: address.city || "",
        state: address.state || "",
        postal_code: address.postal_code || "",
      }
    }
  }

  _loadIndustryCodes(){
    let { processor } = this.state

    if(!processor || !processor.slug){
      return Promise.resolve()
    }

    let processorSlug = processor.slug

    return Backend.getProcessorResource(processorSlug, { action: "industry_code" })
      .then(response => {
        this.setState({
          industryCodes: response || {},
        })
      })
      .catch(error => {
        Notify.error(error.message)
      })
  }

  _loadConnectStatus(){
    let { company, association, processor } = this.state
    if((!company || !company.id) && (!association || !association.id)){
      return Promise.resolve()
    }

    if(!processor || !processor.slug){
      return Promise.resolve()
    }

    return this._handleGetStatus()
      .then(response => {
        response = response && (response.results || response.data) ? (response.results || response.data) : (response || {})
        let connectedRegions = { ...response }
        delete connectedRegions.default_region

        connectedRegions = Object.fromEntries(
          Object.entries(connectedRegions).filter(
            ([_, value]) => !(
              value && Object.keys(value).length === 0
            )
          )
        )

        let regionKeys = Object.keys(connectedRegions)

        this.setState(prevState => ({
          connectedRegions,
          defaultRegion: response.default_region || null,
          showForm: regionKeys.length === 0 || prevState.isEditing,
        }))
      })
      .catch(() => {
        this.setState({
          connectedRegions: {},
          defaultRegion: null,
        })
      })
  }

  _handleGetStatus(){
    let { company, association, processor } = this.state

    if(company){
      return Backend.getConnectData(processor.slug, company.id)
    }else if(association){
      return Backend.getAssociationConnectData(processor.slug, association.id)
    }
  }

  _isFormValid(){
    let { data } = this.state
    let {
      legal_name,
      industry_code,
      phone,
      shopper_statement,
      region,
      default_currency_code,
      address,
    } = data

    let error = null
    if(!legal_name){
      error = "Legal business name is required"
    }
    else if(!industry_code){
      error = "Please select an industry code"
    }
    else if(!phone){
      error = "Phone number is required"
    }
    else if(!Phone.normalize(phone)){
      error = "Please enter a valid phone number with country code"
    }
    else if(!shopper_statement){
      error = "Shopper statement is required"
    }
    else if(shopper_statement.length > 22){
      error = "Shopper statement must be at most 22 characters"
    }
    else if(!default_currency_code){
      error = "Please select the default currency"
    }
    else if(!region){
      error = "Region is required"
    }
    else if(!address.country){
      error = "Country is required"
    }
    else if(!isCountryCodeAllowedForRegion(address.country, region)){
      error = "Selected country is not supported for the chosen region"
    }
    else if(!address.line_1){
      error = "Address line 1 is required"
    }
    else if(!address.city){
      error = "City is required"
    }

    if(!error && ADYEN_STATE_REQUIRED_COUNTRIES.includes(address.country?.toUpperCase() || "") && !address.state){
      error = "State is required for this country"
    }

    if(!error && !address.postal_code){
      error = "Postal code is required"
    }

    if(error){
      Notify.error(error)
      return false
    }

    return true
  }

  _openOnboardingLink(region){
    this._handleGetAction(region)
      .then(response => {
        if(response && response.onboarding_url){
          window.location.href = response.onboarding_url
        }
      })
      .catch(error => {
        Notify.error(error.message)
      })
  }

  _handleGetAction(region){
    let { company, association, processor } = this.state

    if(company){
      return Backend.getProcessorAction(processor.slug, "onboarding_link", {
        region,
        return_url: window.location.href,
      }, company.id)
    }else if(association){
      return Backend.getAssociationProcessorAction(processor.slug, "onboarding_link", {
        region,
        return_url: window.location.href,
      }, association.id)
    }
  }

  _save(){
    let { data } = this.state

    if(!this._isFormValid()){
      return
    }

    data = JSON.parse(JSON.stringify(data))
    data.address.line_2 = data.address.line_2 || null
    data.address.line_3 = data.address.line_3 || null

    this.setState({ loading: true })

    this._handleSave(data)
      .then(() => {
        Notify.success("Adyen Connection Saved")
        this._openOnboardingLink(data.region)
      })
      .catch(error => {
        Notify.error(error.message)
      })
      .finally(() => {
        this.setState({ loading: false })
      })
  }

  _handleSave(data){
    let { company, association, processor } = this.state

    if(company){
      return Backend.updateConnectData(processor.slug, company.id, data)
    }else if(association){
      return Backend.updateAssociationConnectData(processor.slug, association.id, data)
    }
  }

  _resetForNewRegion(){
    let { connectedRegions } = this.state
    let nextRegion = !connectedRegions.eu ? "eu" : "na"

    this.setState({
      data: this._normalizeData({ region: nextRegion }),
      showForm: true,
      isEditing: false,
    })
  }

  _editRegion(region, regionData){
    let requestData = regionData && regionData.request_data ? regionData.request_data : null
    if(!requestData){
      return
    }

    requestData = {
      ...requestData,
      region,
    }

    this.setState({
      data: this._normalizeData(requestData),
      showForm: true,
      isEditing: true,
    })
  }

  _renderConnectedRegions(){
    let {
      connectedRegions,
      defaultRegion,
    } = this.state

    let regionKeys = Object.keys(connectedRegions)
    if(regionKeys.length === 0){
      return null
    }

    return (
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h6 className="mb-0">Connected Regions</h6>
          {regionKeys.length < 2 &&
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => this._resetForNewRegion()}
            >
              Add A New Region
            </button>
          }
        </div>

        {regionKeys.map(region => {
          let regionData = connectedRegions[region] || {}
          return (
            <div
              key={region}
              className="d-flex align-items-center justify-content-between mb-4 p-4 border rounded bg-light"
            >
              <div>
                <strong>{region.toUpperCase()}</strong>
                {defaultRegion === region &&
                  <span className="badge badge-success ml-2">Default</span>
                }
              </div>

              <div>
                {!regionData.onboarded && regionData.request_data &&
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm mr-2"
                    onClick={() => this._editRegion(region, regionData)}
                  >
                    Edit
                  </button>
                }
                {regionData.onboarded &&
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => this._openOnboardingLink(region)}
                  >
                    Continue In Adyen
                  </button>
                }
              </div>
            </div>
          )
        })}
      </Modal.Body>
    )
  }

  render(){
    let {
      data,
      loading,
      loadingStatus,
      connectedRegions,
      industryCodes,
      showForm,
      isEditing,
    } = this.state

    if(loadingStatus){
      return (
        <Modal.Body className="text-center py-10">
          <span className="spinner-border text-primary" />
        </Modal.Body>
      )
    }

    if(!showForm && Object.keys(connectedRegions).length > 0){
      return this._renderConnectedRegions()
    }

    return (
      <>
        <Modal.Body>
          <div className="form-group row">
            <label className="col-3 col-form-label">Legal Business Name*</label>
            <div className="col-9">
              <input
                className="form-control form-control-solid"
                type="text"
                value={data.legal_name}
                onChange={e => {
                  data.legal_name = e.target.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Industry Code*</label>
            <div className="col-9">
              <CustomSelect
                value={Object.keys(industryCodes).map(code => ({
                  code,
                  label: `${code} - ${industryCodes[code]}`
                })).find(option => option.code === data.industry_code) || null}
                options={Object.keys(industryCodes).map(code => ({
                  code,
                  label: `${code} - ${industryCodes[code]}`
                }))}
                placeholder="Select Industry"
                getOptionLabel={option => option.label}
                getOptionValue={option => option.code}
                className="form-control h-auto border-0 form-control-solid c-selectbox"
                onChange={option => {
                  data.industry_code = option.code
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Phone*</label>
            <div className="col-9">
              <PhoneInput
                value={data.phone}
                onChange={(value, phoneData) => {
                  data.phone = `+${phoneData.dialCode}${value.slice(phoneData.dialCode.length)}`
                  this.setState({ data })
                }}
                containerStyle={{ width: "100%" }}
                inputStyle={{ width: "100%" }}
                inputProps={{
                  autoComplete: 'off',
                  className: 'form-control form-control-solid',
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Shopper Statement*</label>
            <div className="col-9">
              <input
                className="form-control form-control-solid"
                type="text"
                maxLength={22}
                value={data.shopper_statement}
                onChange={e => {
                  data.shopper_statement = e.target.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Default Currency*</label>
            <div className="col-9">
              <CustomSelect
                value={CURRENCY_OPTIONS.find(option => option.value === data.default_currency_code) || null}
                options={CURRENCY_OPTIONS}
                placeholder="Default Currency"
                menuPortalTarget={null}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.value}
                className="form-control h-auto border-0 form-control-solid c-selectbox"
                onChange={option => {
                  data.default_currency_code = option.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Region*</label>
            <div className="col-9">
              <CustomSelect
                value={REGION_OPTIONS.find(option => option.value === data.region) || null}
                options={REGION_OPTIONS.filter(option => {
                  if(isEditing){
                    return option.value === data.region
                  }
                  if(option.value === "eu" && connectedRegions.eu){
                    return false
                  }
                  if(option.value === "na" && connectedRegions.na){
                    return false
                  }
                  return true
                })}
                placeholder="Select Region"
                isDisabled={isEditing}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.value}
                className="form-control h-auto border-0 form-control-solid c-selectbox"
                onChange={option => {
                  data.region = option.value
                  data.address.country = ""
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Country*</label>
            <div className="col-9">
              <CountrySelect
                value={data.address?.country}
                region={data.region}
                className="form-control-solid"
                onSelected={option => {
                  data.address.country = option ? option.value : ""
                  data.address.state = ""
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Address Line 1*</label>
            <div className="col-9">
              <input
                className="form-control form-control-solid"
                type="text"
                value={data.address?.line_1}
                onChange={e => {
                  data.address.line_1 = e.target.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Address Line 2</label>
            <div className="col-9">
              <input
                className="form-control form-control-solid"
                type="text"
                value={data.address?.line_2}
                onChange={e => {
                  data.address.line_2 = e.target.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Address Line 3</label>
            <div className="col-9">
              <input
                className="form-control form-control-solid"
                type="text"
                value={data.address?.line_3}
                onChange={e => {
                  data.address.line_3 = e.target.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">City*</label>
            <div className="col-9">
              <input
                className="form-control form-control-solid"
                type="text"
                value={data.address?.city}
                onChange={e => {
                  data.address.city = e.target.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">State / Province{ADYEN_STATE_REQUIRED_COUNTRIES.includes(data.address?.country?.toUpperCase() || "") ? "*" : ""}</label>
            <div className="col-9">
              {
                data.address?.country === "US" ?
                  <>
                    <CustomSelect
                      value={US_STATES.find(state => state.value === data.address?.state) || null}
                      options={US_STATES}
                      placeholder="Select A State"
                      getOptionLabel={state => state.label}
                      getOptionValue={state => state.value}
                      className="form-control h-auto border-0 form-control-solid c-selectbox"
                      onChange={state => {
                        data.address.state = state.value
                        this.setState({ data })
                      }}
                    />
                  </>
                :
                  <>
                    <input
                      className="form-control form-control-solid"
                      type="text"
                      value={data.address?.state}
                      onChange={e => {
                        data.address.state = e.target.value
                        this.setState({ data })
                      }}
                    />
                  </>
              }
            </div>
          </div>

          <div className="form-group row">
            <label className="col-3 col-form-label">Postal Code*</label>
            <div className="col-9">
              <input
                className="form-control form-control-solid"
                type="text"
                value={data.address?.postal_code}
                onChange={e => {
                  data.address.postal_code = e.target.value
                  this.setState({ data })
                }}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          {Object.keys(connectedRegions).length > 0 &&
            <button
              type="button"
              className="btn btn-outline-primary ml-3"
              onClick={() => this.setState({ showForm: false, isEditing: false })}
            >
              Cancel
            </button>
          }

          <button
            type="button"
            disabled={loading}
            className="btn btn-primary ml-3"
            onClick={() => this._save()}
          >
            Save & Continue
          </button>
        </Modal.Footer>
      </>
    )
  }
}

const US_STATES = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];
